import { supabase } from './supabase'

/**
 * Web push subscribe/unsubscribe.
 *
 * The VAPID public key is exactly that — public. It travels in every push
 * subscription and identifies the sender; the private half never leaves the
 * Netlify environment. So VITE_ is the right prefix here, unlike the service
 * role key.
 */
const VAPID_PUBLIC = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined

export type PushState = 'unsupported' | 'unconfigured' | 'denied' | 'off' | 'on'

/**
 * iOS is the reason this is fiddly. Safari only allows push from a PWA that
 * has been added to the home screen — in a normal Safari tab the API is
 * missing entirely, and there is no prompt to show. Telling an iPhone user to
 * "allow notifications" when the button cannot work is worse than saying
 * nothing, so the caller gets `unsupported` and shows the install hint instead.
 */
export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported'
  if (!VAPID_PUBLIC) return 'unconfigured'
  if (Notification.permission === 'denied') return 'denied'
  const reg = await navigator.serviceWorker.getRegistration()
  const sub = await reg?.pushManager.getSubscription()
  return sub ? 'on' : 'off'
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
  return Uint8Array.from(raw, (c) => c.charCodeAt(0))
}

/** Browser's key material arrives as raw ArrayBuffers; the server wants base64url. */
function b64(buf: ArrayBuffer | null): string {
  if (!buf) return ''
  const bytes = new Uint8Array(buf)
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported'
  if (!VAPID_PUBLIC) return 'unconfigured'

  // Must be called from a user gesture, or Safari rejects it outright.
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off'

  const reg = await navigator.serviceWorker.ready
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC) as BufferSource,
    }))

  const { data: user } = await supabase.auth.getUser()
  const uid = user.user?.id
  if (!uid) throw new Error('Not signed in')

  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      endpoint: sub.endpoint,
      user_id: uid,
      p256dh: b64(sub.getKey('p256dh')),
      auth_key: b64(sub.getKey('auth')),
      user_agent: navigator.userAgent.slice(0, 300),
      failures: 0,
    },
    { onConflict: 'endpoint' },
  )
  if (error) throw error
  return 'on'
}

export async function disablePush(): Promise<PushState> {
  const reg = await navigator.serviceWorker.getRegistration()
  const sub = await reg?.pushManager.getSubscription()
  if (sub) {
    // Delete the row first. If unsubscribe() succeeded and the delete then
    // failed, the job would keep pushing to an endpoint nobody is listening on
    // until the browser 410s it away.
    await supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint)
    await sub.unsubscribe()
  }
  return 'off'
}
